// Offline IndexedDB store + sync queue for SeedKeepers field mode.
// Drafts live entirely on-device until they sync to /api/collections.

import { openDB } from "idb";
import { api } from "./api";

const DB_NAME = "seedkeepers-offline";
const DB_VERSION = 1;
const DRAFTS = "drafts";

let _dbPromise;

function getDb() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DRAFTS)) {
          const store = db.createObjectStore(DRAFTS, { keyPath: "local_id" });
          store.createIndex("status", "status");
          store.createIndex("created_at", "created_at");
        }
      },
    });
  }
  return _dbPromise;
}

export async function saveDraft(payload, status = "pending") {
  const db = await getDb();
  const local_id = payload.local_id || `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const record = {
    local_id,
    payload,
    status, // "pending" | "syncing" | "synced" | "error"
    error: null,
    server_id: null,
    server_collection_id: null,
    created_at: payload.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  await db.put(DRAFTS, record);
  notify();
  return record;
}

export async function listDrafts() {
  const db = await getDb();
  const all = await db.getAll(DRAFTS);
  return all.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

export async function pendingCount() {
  const db = await getDb();
  const tx = db.transaction(DRAFTS, "readonly");
  const idx = tx.store.index("status");
  let count = 0;
  for await (const cursor of idx.iterate("pending")) {
    void cursor;
    count += 1;
  }
  return count;
}

export async function deleteDraft(local_id) {
  const db = await getDb();
  await db.delete(DRAFTS, local_id);
  notify();
}

export async function markStatus(local_id, status, extra = {}) {
  const db = await getDb();
  const existing = await db.get(DRAFTS, local_id);
  if (!existing) return;
  const updated = { ...existing, ...extra, status, updated_at: new Date().toISOString() };
  await db.put(DRAFTS, updated);
  notify();
}

// ---------- Sync engine ----------
let syncing = false;

export async function syncDrafts({ force = false } = {}) {
  if (syncing && !force) return { skipped: true };
  if (!navigator.onLine && !force) return { skipped: true, reason: "offline" };
  syncing = true;
  const db = await getDb();
  const all = await db.getAll(DRAFTS);
  const pending = all.filter((d) => d.status === "pending" || d.status === "error");
  const results = { synced: 0, failed: 0, total: pending.length };

  for (const draft of pending) {
    await markStatus(draft.local_id, "syncing");
    try {
      const { data } = await api.post("/collections", draft.payload);
      await markStatus(draft.local_id, "synced", {
        server_id: data.id,
        server_collection_id: data.collection_id,
        error: null,
      });
      results.synced += 1;
    } catch (e) {
      await markStatus(draft.local_id, "error", {
        error: e.response?.data?.detail || e.message || "Sync failed",
      });
      results.failed += 1;
    }
  }
  syncing = false;
  notify();
  return results;
}

// ---------- Pub/sub for UI ----------
const listeners = new Set();

function notify() {
  for (const l of listeners) {
    try { l(); } catch (_e) { /* ignore */ }
  }
}

export function onChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Auto-sync when we come back online
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    syncDrafts().then(notify);
  });
}
